import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { useAppDispatch } from "@redux/hooks";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { setActiveRole } from "@user-management-context/write/application/use-cases/active-role/set-active-role";
import { writeActiveRoleToStorage } from "@shared-kernel/core/active-role/active-role";
import { Card, CardContent } from "@shared-kernel/primary/shared/shadcn/ui/card";
import { StudentOnboardingContainer } from "@user-management-context/primary/onboarding/student/student-onboarding.container";
import { TeacherOnboardingContainer } from "@user-management-context/primary/onboarding/teacher/teacher-onboarding.container";
import { ProviderOnboardingContainer } from "@user-management-context/primary/onboarding/provider/provider-onboarding.container";

const isRole = (value: string | undefined): value is ROLES =>
  value === ROLES.STUDENT || value === ROLES.TEACHER || value === ROLES.PROVIDER;

const renderRoleContainer = (role: ROLES) => {
  if (role === ROLES.STUDENT) return <StudentOnboardingContainer />;
  if (role === ROLES.TEACHER) return <TeacherOnboardingContainer />;
  return <ProviderOnboardingContainer />;
};

export const RoleOnboardingPage = () => {
  const params = useParams<{ role: string }>();
  const { user } = useSession();
  const dispatch = useAppDispatch();
  const role = isRole(params.role) ? params.role : null;

  useEffect(() => {
    if (role) {
      dispatch(setActiveRole(role));
      writeActiveRoleToStorage(role);
    }
  }, [role, dispatch]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.isAdmin) return <Navigate to="/" replace />;
  if (!role) return <Navigate to="/onboarding" replace />;

  const userRole = user.roles.find(r => r.role === role);
  if (!userRole || userRole.status !== "pending_onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-5">{renderRoleContainer(role)}</CardContent>
    </Card>
  );
};
